import { QUERY_REFETCH_INTERVAL } from '@/config/consts'
import { usePageChainId } from '@/domain/hooks/usePageChainId'
import type { Facilitator, Reserve } from '@/domain/market-info/marketInfo'
import { useMarketInfo } from '@/domain/market-info/useMarketInfo'
import { Percentage } from '@/domain/types/NumericValues'
import { useELend } from '@/features/elend/logic/useELend'
import { getVaultMarket } from '@/features/evault-details/data/products'
import { useUnifiedEarnOpportunities } from '@/features/unified-earn/logic/useUnifiedEarnOpportunities'
import type { YieldOpportunity } from '@/features/unified-earn/types'
import { TokenSymbolEnum, getAddressBySymbol } from '@/utils/tokens'
import { useMemo } from 'react'
import { useAccount } from 'wagmi'
import { shortMarketLabel } from '../components/shared'
import type { ArbitrageOpportunity, BorrowOpportunity, DepositOpportunity, LiquidityInfo } from '../types'
import { borrowRateAfterTrade, buildDeltaSteps, computeMaxTradeSize, supplyRateAfterTrade } from './rateImpact'

// ---------------------------------------------------------------------------
// Asset grouping
// ---------------------------------------------------------------------------

const STABLECOIN_SYMBOLS = new Set([
  'USDXL',
  'USDC',
  'USDT',
  'USDT0',
  'USD_T0',
  'USDE',
  'SUSDE',
  'DAI',
  'SDAI',
  'FEUSD',
])

function normalizeAssetForMatching(symbol: string): string {
  const upper = symbol.toUpperCase()
  if (STABLECOIN_SYMBOLS.has(upper)) return 'STABLECOIN'
  if (
    upper === 'WHYPE' ||
    upper === 'HYPE' ||
    upper === 'STKHYPE' ||
    upper === 'WSTHYPE' ||
    upper === 'LSTHYPE' ||
    upper === 'BEHYPE'
  )
    return 'HYPE'
  if (upper === 'WETH' || upper === 'ETH') return 'ETH'
  if (upper === 'WBTC' || upper === 'BTC' || upper === 'TBTC' || upper === 'LBTC') return 'BTC'
  return upper
}

function percentageToNumber(p: Percentage): number {
  return Number(p) * 100
}

/** Aave v3 rate params (baseRate, slope1, slope2) are stored in RAY (1e27). */
const RAY = 1e27

// ---------------------------------------------------------------------------
// Liquidity builders
// ---------------------------------------------------------------------------

function buildPooledBorrowLiquidity(reserve: Reserve): LiquidityInfo {
  const totalSupplyUsd = reserve.totalLiquidityUSD.toNumber()
  const totalBorrowUsd = reserve.totalDebtUSD.toNumber()
  const availableUsd = Math.max(0, reserve.availableLiquidityUSD.toNumber())
  const utilization = reserve.utilizationRate ? Number(reserve.utilizationRate) : 0

  return {
    totalSupplyUsd,
    totalBorrowUsd,
    availableUsd,
    utilization,
    rateModel: {
      type: 'aave-kink',
      baseRate: Number(reserve.baseVariableBorrowRate) / RAY,
      slope1: Number(reserve.variableRateSlope1) / RAY,
      slope2: Number(reserve.variableRateSlope2) / RAY,
      optimalUtilization: Number(reserve.optimalUtilizationRate), // already 0-1
      reserveFactor: Number(reserve.reserveFactor), // already 0-1
    },
  }
}

function buildPooledSupplyLiquidity(reserve: Reserve): LiquidityInfo {
  return buildPooledBorrowLiquidity(reserve) // same model, different function used in computeSupplyRate
}

/**
 * Build liquidity for USDXL (GHO fork).
 * Uses the reserve's Aave kink model for rate math but the facilitator
 * bucket for available-to-borrow (since USDXL is minted, not deposited).
 */
function buildUsdxlBorrowLiquidity(reserve: Reserve, facilitator: Facilitator): LiquidityInfo {
  const borrowLimit = facilitator.borrowLimit.toNumber()
  const currentBorrowed = facilitator.currentBorrowed.toNumber()
  const availableToBorrow = facilitator.availableToBorrow.toNumber()
  const util = borrowLimit > 0 ? currentBorrowed / borrowLimit : 0

  // Price of USDXL ≈ $1 (stablecoin), use reserve price if available
  const priceUsd = reserve.priceInUSD.toNumber()
  const availableUsd = availableToBorrow * priceUsd
  const totalSupplyUsd = borrowLimit * priceUsd
  const totalBorrowUsd = currentBorrowed * priceUsd

  return {
    totalSupplyUsd,
    totalBorrowUsd,
    availableUsd,
    utilization: util,
    rateModel: {
      type: 'aave-kink',
      baseRate: Number(reserve.baseVariableBorrowRate) / RAY,
      slope1: Number(reserve.variableRateSlope1) / RAY,
      slope2: Number(reserve.variableRateSlope2) / RAY,
      optimalUtilization: Number(reserve.optimalUtilizationRate), // already 0-1
      reserveFactor: Number(reserve.reserveFactor), // already 0-1
    },
  }
}

function buildELendLiquidity(
  vault: {
    totalAssets?: { toNumber(): number }
    totalBorrowed?: { toNumber(): number }
    borrowAPY?: number
    supplyAPY?: number
    utilization?: number
    tvlUsd?: { toNumber(): number }
  },
  isBorrow: boolean,
): LiquidityInfo {
  const totalAssetsNum = vault.totalAssets?.toNumber() ?? 0
  const totalBorrowedNum = vault.totalBorrowed?.toNumber() ?? 0
  const tvlUsd = vault.tvlUsd?.toNumber() ?? 0
  // Estimate price from tvlUsd / totalAssets
  const priceEstimate = totalAssetsNum > 0 ? tvlUsd / totalAssetsNum : 1
  const totalSupplyUsd = totalAssetsNum * priceEstimate
  const totalBorrowUsd = totalBorrowedNum * priceEstimate
  const availableUsd = Math.max(0, totalSupplyUsd - totalBorrowUsd)
  const util = vault.utilization ?? (totalAssetsNum > 0 ? totalBorrowedNum / totalAssetsNum : 0)

  const rate = isBorrow ? vault.borrowAPY ?? 0 : vault.supplyAPY ?? 0

  return {
    totalSupplyUsd,
    totalBorrowUsd,
    availableUsd,
    utilization: util,
    rateModel: {
      type: 'linear-approx',
      currentRate: rate,
      currentUtilization: util,
    },
  }
}

function buildDepositLiquidityFromEarnOpp(opp: YieldOpportunity, platformAPY: number): LiquidityInfo {
  const tvlUsd = opp.tvlUsd?.toNumber() ?? 0

  // When TVL is 0 or missing, use a conservative fallback so we can still
  // estimate rate impact (the linear model degrades gracefully).
  const effectiveTvl = Math.max(tvlUsd, 1)

  // Use a consistent effective utilization for both totalBorrowUsd and the rate
  // model. When utilization is 0/undefined (common for elend meta-vaults), fall
  // back to 0.5 so the linear model can still compute meaningful rate changes.
  const rawUtil = opp.utilization ?? 0
  const effectiveUtil = rawUtil > 0 ? rawUtil : 0.5
  const totalBorrowUsd = effectiveTvl * effectiveUtil

  return {
    totalSupplyUsd: effectiveTvl,
    totalBorrowUsd,
    availableUsd: effectiveTvl,
    utilization: effectiveUtil,
    rateModel: {
      type: 'linear-approx',
      currentRate: platformAPY,
      currentUtilization: effectiveUtil,
    },
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseArbitrageOpportunitiesResult {
  opportunities: ArbitrageOpportunity[]
  isLoading: boolean
  borrowSources: BorrowOpportunity[]
  depositSources: DepositOpportunity[]
}

export function useArbitrageOpportunities(): UseArbitrageOpportunitiesResult {
  const { address: userAddress } = useAccount()
  const { chainId } = usePageChainId()

  // Deposit side: leverage unified earn
  const { opportunities: earnOpportunities, isLoading: isLoadingEarn } = useUnifiedEarnOpportunities()

  // Borrow side sources
  const { marketInfo, isPending: isLoadingPooled } = useMarketInfo({
    chainId,
    refetchInterval: QUERY_REFETCH_INTERVAL,
  })

  const { vaults: elendVaults, isLoading: isLoadingELend } = useELend(userAddress)

  // Derive USDXL borrow rate directly from marketInfo (avoids a redundant useMarketInfo subscription)
  const usdxlBorrowAPY = useMemo(() => {
    try {
      const usdxlAddress = getAddressBySymbol(TokenSymbolEnum.USDXL)
      if (!usdxlAddress) return undefined

      const reserve = marketInfo?.reserves?.find((r) => r.token.address.toLowerCase() === usdxlAddress.toLowerCase())
      if (!reserve?.variableBorrowApy) return undefined

      return Number(reserve.variableBorrowApy) * 100
    } catch {
      return undefined
    }
  }, [marketInfo])

  // Build a reserve lookup for getting pooled supply-side liquidity
  const reserveByAddress = useMemo(() => {
    const map = new Map<string, Reserve>()
    if (marketInfo?.reserves) {
      for (const r of marketInfo.reserves) {
        map.set(r.token.address.toLowerCase(), r)
      }
    }
    return map
  }, [marketInfo])

  // --- Build borrow opportunities with liquidity ---
  const borrowSources = useMemo((): BorrowOpportunity[] => {
    const borrows: BorrowOpportunity[] = []

    // 1. Pooled (Aave v3) — skip USDXL here; it's handled in section 4 with facilitator liquidity
    const usdxlAddressLower = getAddressBySymbol(TokenSymbolEnum.USDXL)?.toLowerCase()
    if (marketInfo?.reserves) {
      for (const reserve of marketInfo.reserves) {
        if (reserve.borrowEligibilityStatus === 'no') continue
        if (usdxlAddressLower && reserve.token.address.toLowerCase() === usdxlAddressLower) continue
        const apy = reserve.variableBorrowApy ? percentageToNumber(reserve.variableBorrowApy) : undefined
        if (apy === undefined) continue

        borrows.push({
          source: 'pooled',
          address: reserve.token.address,
          asset: reserve.token.symbol.toString(),
          token: reserve.token,
          borrowAPY: apy,
          name: `Pooled ${reserve.token.symbol}`,
          liquidity: buildPooledBorrowLiquidity(reserve),
        })
      }
    }

    // 2. ELend (Euler v2)
    for (const vault of elendVaults) {
      if (!vault.borrowAPY || vault.borrowAPY <= 0) continue
      const symbol = vault.assetSymbol ?? vault.symbol
      const market = vault.market ?? getVaultMarket(vault.address)
      const marketLabel = shortMarketLabel(market)
      borrows.push({
        source: 'elend',
        address: vault.address,
        asset: symbol,
        token: vault.token,
        borrowAPY: vault.borrowAPY,
        name: `${marketLabel} ${symbol}`,
        liquidity: buildELendLiquidity(vault, true),
        market,
      })
    }

    // 3. USDXL (GHO fork — uses Aave kink model + facilitator bucket for liquidity)
    if (usdxlBorrowAPY !== undefined && usdxlBorrowAPY > 0) {
      const usdxlAddress = getAddressBySymbol(TokenSymbolEnum.USDXL) as `0x${string}`
      const usdxlReserve = marketInfo?.reserves?.find(
        (r) => r.token.address.toLowerCase() === usdxlAddress.toLowerCase(),
      )
      const facilitator = marketInfo?.facilitator

      const usdxlLiquidity: LiquidityInfo | undefined =
        usdxlReserve && facilitator
          ? buildUsdxlBorrowLiquidity(usdxlReserve, facilitator)
          : usdxlReserve
            ? buildPooledBorrowLiquidity(usdxlReserve)
            : undefined

      borrows.push({
        source: 'usdxl',
        address: usdxlAddress,
        asset: 'USDXL',
        token: usdxlReserve?.token,
        borrowAPY: usdxlBorrowAPY,
        name: 'USDXL Mint',
        liquidity: usdxlLiquidity,
      })
    }

    return borrows
  }, [marketInfo, elendVaults, usdxlBorrowAPY])

  // --- Build deposit opportunities with liquidity ---
  const depositSources = useMemo((): DepositOpportunity[] => {
    return earnOpportunities
      .filter((opp) => opp.supplyAPY > 0 && opp.source !== 'evault')
      .map((opp) => {
        // Strip intrinsic yield (staking, PT, etc.) — only count platform-generated yield.
        // sourceData.lendingAPY is the decompounded platform-only portion;
        // sourceData.intrinsicAPY is the staking/PT yield baked into supplyAPY.
        const sd = opp.sourceData
        let platformAPY = opp.supplyAPY
        if ((sd.type === 'elend' || sd.type === 'pooled') && sd.intrinsicAPY && sd.intrinsicAPY > 0) {
          platformAPY = sd.lendingAPY ?? Math.max(0, opp.supplyAPY - sd.intrinsicAPY)
        }

        // Try to get precise liquidity from the pooled reserve if available
        let liquidity: LiquidityInfo | undefined
        if (opp.source === 'pooled') {
          const reserve = reserveByAddress.get(opp.address.toLowerCase())
          if (reserve) {
            liquidity = buildPooledSupplyLiquidity(reserve)
          }
        }
        // For isolated, try to get liquidity from sourceData
        if (!liquidity && opp.sourceData.type === 'isolated') {
          const isd = opp.sourceData
          const tvlUsd = Math.max(opp.tvlUsd?.toNumber() ?? 0, 1)
          const rawUtil = isd.utilization / 100
          const effectiveUtil = rawUtil > 0 ? rawUtil : 0.5
          const totalBorrowed = tvlUsd * effectiveUtil
          liquidity = {
            totalSupplyUsd: tvlUsd,
            totalBorrowUsd: totalBorrowed,
            availableUsd: tvlUsd,
            utilization: effectiveUtil,
            rateModel: {
              type: 'linear-approx',
              currentRate: platformAPY,
              currentUtilization: effectiveUtil,
            },
          }
        }
        // Fallback for elend/any source without liquidity yet
        if (!liquidity) {
          liquidity = buildDepositLiquidityFromEarnOpp(opp, platformAPY)
        }

        // Resolve display name for elend sources
        let displayName = opp.name
        if (opp.source === 'elend') {
          displayName = `${shortMarketLabel(opp.market)} ${opp.assetSymbol}`
        }

        // For isolated pairs, extract "COLLATERAL/BORROW" pair name
        const pairName =
          opp.source === 'isolated' && opp.sourceData.type === 'isolated'
            ? `${opp.sourceData.collateralToken.symbol}/${opp.sourceData.borrowToken.symbol}`
            : undefined

        return {
          source: opp.source,
          address: opp.address,
          asset: opp.assetSymbol,
          token: opp.token,
          depositAPY: platformAPY,
          name: displayName,
          liquidity,
          market: opp.market,
          pairName,
        }
      })
  }, [earnOpportunities, reserveByAddress])

  // --- Match and compute arbitrage with max trade sizes ---
  const opportunities = useMemo((): ArbitrageOpportunity[] => {
    const arbs: ArbitrageOpportunity[] = []

    for (const borrow of borrowSources) {
      const borrowGroup = normalizeAssetForMatching(borrow.asset)

      for (const deposit of depositSources) {
        const depositGroup = normalizeAssetForMatching(deposit.asset)
        if (borrowGroup !== depositGroup) continue

        const spread = deposit.depositAPY - borrow.borrowAPY
        if (spread <= 0) continue

        // Skip self-referencing
        if (borrow.source === deposit.source && borrow.address === deposit.address) continue

        // Compute max trade size if we have liquidity on both sides
        let maxTradeUsd: number | undefined
        let borrowAPYAtMax: number | undefined
        let depositAPYAtMax: number | undefined
        let spreadAtMax: number | undefined
        let deltaSteps: import('../types').DeltaStep[] = []

        if (borrow.liquidity && deposit.liquidity) {
          const result = computeMaxTradeSize(borrow.liquidity, deposit.liquidity)
          if (result) {
            maxTradeUsd = result.maxTradeUsd
            borrowAPYAtMax = result.borrowAPYAtMax
            depositAPYAtMax = result.depositAPYAtMax
            spreadAtMax = result.spreadAtMax
            deltaSteps = result.deltaSteps
          } else if (borrow.liquidity.availableUsd > 0) {
            // Crossover model disagrees with raw APYs (e.g. compound vs simple rate).
            // Still compute steps using available liquidity as the trade cap.
            const cap = borrow.liquidity.availableUsd
            maxTradeUsd = cap
            const bAtMax = borrowRateAfterTrade(borrow.liquidity, cap)
            const dAtMax = supplyRateAfterTrade(deposit.liquidity, cap)
            borrowAPYAtMax = bAtMax
            depositAPYAtMax = dAtMax
            spreadAtMax = dAtMax - bAtMax
            deltaSteps = buildDeltaSteps(borrow.liquidity, deposit.liquidity, cap)
          }
        }

        arbs.push({
          id: `${borrow.source}-${borrow.address}-${deposit.source}-${deposit.address}`,
          borrow,
          deposit,
          spread,
          asset: borrowGroup === 'STABLECOIN' ? `${borrow.asset} -> ${deposit.asset}` : borrow.asset,
          maxTradeUsd,
          borrowAPYAtMax,
          depositAPYAtMax,
          spreadAtMax,
          deltaSteps,
        })
      }
    }

    // Sort by spread descending
    arbs.sort((a, b) => b.spread - a.spread)

    return arbs
  }, [borrowSources, depositSources])

  const isLoading = isLoadingEarn || isLoadingPooled || isLoadingELend

  return {
    opportunities,
    isLoading,
    borrowSources,
    depositSources,
  }
}
